/* Reusable Excel/CSV import wizard with column mapping.
 * iter52 — used by Students and Personnel pages.
 *
 * Props:
 *   - open, onOpenChange
 *   - title           — dialog header title
 *   - kind            — 'students' | 'teachers'
 *   - api             — { importPreview, importFinal } functions returning axios promises
 *   - onImported()    — callback invoked with the final report so the parent can toast/refresh
 *
 * The wizard is 3-step:
 *   1. Pick file (.csv/.xlsx) + optional dry-run toggle → hits `preview_only`
 *   2. Review detected columns + adjust mapping if needed
 *   3. Confirm & submit → runs the real import and displays the report
 */
import { useState, useMemo, useRef } from 'react';
import { toast } from 'sonner';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from './ui/dialog';
import { Button } from './ui/button';
import { Label } from './ui/label';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from './ui/table';
import { UploadSimple, ArrowRight, CheckCircle, XCircle, MagicWand } from '@phosphor-icons/react';

const TARGET_LABELS = {
  first_name: 'Prénom',
  last_name: 'Nom',
  date_of_birth: 'Date de naissance',
  birth_place: 'Lieu de naissance',
  nationality: 'Nationalité',
  class_id: 'Classe',
  cycle: 'Cycle',
  father_name: 'Père',
  mother_name: 'Mère',
  parent_contact: 'Téléphone parent',
  email: 'E-mail',
  address: 'Adresse',
  // Teachers
  contact: 'Téléphone',
  subject: 'Matière',
  function: 'Fonction',
  contract_type: 'Type de contrat',
  salary: 'Salaire',
  birthdate: 'Date de naissance',
  hire_date: "Date d'embauche",
  // Staff
  staff_type: 'Catégorie (canteen/transport/educator/cleaning/direction)',
};

const REQUIRED_FIELDS_BY_KIND = {
  students: ['first_name', 'last_name'],
  teachers: ['first_name', 'last_name'],
  staff: ['first_name', 'last_name'],
};

export default function ImportWizard({ open, onOpenChange, title, kind, api, onImported }) {
  const [step, setStep] = useState(1);
  const [file, setFile] = useState(null);
  const [dryRun, setDryRun] = useState(true);
  const [preview, setPreview] = useState(null);
  const [mapping, setMapping] = useState({});
  const [savedMappings, setSavedMappings] = useState([]);
  const [saveLabel, setSaveLabel] = useState('');
  const [busy, setBusy] = useState(false);
  const [report, setReport] = useState(null);
  const fileRef = useRef(null);

  const requiredFields = REQUIRED_FIELDS_BY_KIND[kind] || [];

  const reset = () => {
    setStep(1); setFile(null); setDryRun(true);
    setPreview(null); setMapping({}); setSavedMappings([]); setSaveLabel('');
    setReport(null); setBusy(false);
    if (fileRef.current) fileRef.current.value = '';
  };

  const handleClose = () => { onOpenChange(false); setTimeout(reset, 300); };

  const runPreview = async () => {
    if (!file) { toast.error('Sélectionnez un fichier'); return; }
    setBusy(true);
    try {
      const { data } = await api.importPreview(file);
      setPreview(data);
      setMapping({ ...(data.suggested_mapping || {}) });
      setSavedMappings(data.saved_mappings || []);
      setStep(2);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur analyse du fichier');
    } finally { setBusy(false); }
  };

  const applySavedMapping = (label) => {
    if (!label) return;
    const m = savedMappings.find((s) => s.label === label);
    if (m) {
      setMapping({ ...(m.mapping || {}) });
      toast.success(`Mapping « ${label} » appliqué`);
    }
  };

  const runFinal = async () => {
    // Validate required fields are mapped
    const missing = requiredFields.filter((f) => !mapping[f]);
    if (missing.length) {
      toast.error(`Colonnes requises non mappées : ${missing.map((f) => TARGET_LABELS[f] || f).join(', ')}`);
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.importFinal(file, {
        dryRun,
        columnMapping: mapping,
        saveMappingAs: saveLabel.trim() || undefined,
      });
      setReport(data);
      setStep(3);
      onImported?.(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur import');
    } finally { setBusy(false); }
  };

  const targetFields = useMemo(
    () => (preview?.target_fields || Object.keys(TARGET_LABELS)),
    [preview],
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="import-wizard-dialog">
        <DialogHeader>
          <DialogTitle className="font-outfit flex items-center gap-2">
            <UploadSimple size={22} className="text-[#0052FF]" weight="duotone" /> {title}
          </DialogTitle>
          <DialogDescription>
            <div className="flex items-center gap-2 mt-2 text-xs">
              {[1, 2, 3].map((n) => (
                <div key={n} className={`flex items-center gap-1 ${step >= n ? 'text-[#0052FF] font-semibold' : 'text-[#94A3B8]'}`}>
                  <span className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold ${
                    step >= n ? 'bg-[#0052FF] text-white' : 'bg-[#E2E8F0] text-[#64748B]'
                  }`}>{n}</span>
                  {n === 1 && 'Fichier'} {n === 2 && 'Colonnes'} {n === 3 && 'Rapport'}
                  {n < 3 && <ArrowRight size={12} className="ml-1" />}
                </div>
              ))}
            </div>
          </DialogDescription>
        </DialogHeader>

        {/* STEP 1 — Pick file */}
        {step === 1 && (
          <div className="space-y-4">
            <Label>Fichier CSV ou Excel (.csv, .xlsx)</Label>
            <input ref={fileRef} type="file"
              accept=".csv,.xlsx,.xlsm,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              data-testid="wizard-file-input"
              className="block w-full text-sm border border-[#E2E8F0] rounded-md p-2 file:mr-3 file:px-3 file:py-1.5 file:bg-[#F1F5F9] file:border-0 file:rounded" />
            {file && (
              <div className="text-xs bg-[#F1F5F9] p-2 rounded">
                <b>{file.name}</b> · {(file.size / 1024).toFixed(1)} Ko
              </div>
            )}
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={dryRun} onChange={(e) => setDryRun(e.target.checked)}
                data-testid="wizard-dryrun-checkbox" />
              Mode simulation (dry-run) — n&apos;insère rien, montre juste ce qui serait fait
            </label>
            <div className="text-xs bg-blue-50 border border-blue-200 rounded p-3 text-blue-800">
              <MagicWand size={14} className="inline mr-1" weight="fill" />
              Le fichier sera d&apos;abord analysé pour détecter les colonnes ;
              vous pourrez ensuite ajuster le mapping avant l&apos;import final.
            </div>
          </div>
        )}

        {/* STEP 2 — Column mapping */}
        {step === 2 && preview && (
          <div className="space-y-4">
            <div className="text-xs text-[#64748B] bg-[#F8FAFC] p-2 rounded">
              <b>{preview.filename}</b> · encodage <b>{preview.encoding}</b> · séparateur <b>{preview.delimiter === '\t' ? 'TAB' : preview.delimiter}</b> · <b>{preview.row_count}</b> ligne(s) détectée(s)
            </div>
            {savedMappings.length > 0 && (
              <div className="p-3 bg-blue-50 border border-blue-200 rounded" data-testid="wizard-saved-mappings">
                <Label className="text-blue-900">Mapping enregistré (1 clic)</Label>
                <div className="flex gap-2 mt-1">
                  <select
                    onChange={(e) => applySavedMapping(e.target.value)}
                    className="flex-1 h-9 rounded border border-blue-300 px-2 text-sm bg-white"
                    defaultValue=""
                    data-testid="wizard-saved-mapping-select"
                  >
                    <option value="">— Réutiliser un mapping enregistré —</option>
                    {savedMappings.map((m) => (
                      <option key={m.label} value={m.label}>{m.label}</option>
                    ))}
                  </select>
                </div>
              </div>
            )}
            <div>
              <Label>Correspondance des colonnes</Label>
              <p className="text-xs text-[#64748B] mb-2">
                Chaque champ attendu à gauche doit être associé à une colonne de votre fichier à droite.
                Les champs marqués <span className="text-red-600 font-bold">*</span> sont obligatoires.
              </p>
              <div className="max-h-72 overflow-y-auto border border-[#E2E8F0] rounded">
                <table className="w-full text-sm">
                  <thead className="bg-[#F8FAFC] sticky top-0">
                    <tr>
                      <th className="text-left p-2 font-semibold">Champ attendu</th>
                      <th className="text-left p-2 font-semibold">Colonne du fichier</th>
                    </tr>
                  </thead>
                  <tbody>
                    {targetFields.map((f) => (
                      <tr key={f} className="border-t border-[#E2E8F0]">
                        <td className="p-2">
                          {TARGET_LABELS[f] || f}
                          {requiredFields.includes(f) && <span className="text-red-600 font-bold ml-1">*</span>}
                        </td>
                        <td className="p-2">
                          <select
                            value={mapping[f] || ''}
                            onChange={(e) => setMapping((m) => ({ ...m, [f]: e.target.value }))}
                            className={`w-full h-9 rounded-md border px-2 text-sm bg-white ${
                              requiredFields.includes(f) && !mapping[f]
                                ? 'border-red-300 bg-red-50'
                                : 'border-[#E2E8F0]'
                            }`}
                            data-testid={`wizard-map-${f}`}
                          >
                            <option value="">— Ignorer —</option>
                            {(preview.headers || []).map((h) => (
                              <option key={h} value={h}>{h}</option>
                            ))}
                          </select>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
            {preview.sample_rows?.length > 0 && (
              <div>
                <Label>Aperçu (5 premières lignes)</Label>
                <div className="mt-1 max-h-40 overflow-auto border border-[#E2E8F0] rounded text-xs">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        {(preview.headers || []).map((h) => (
                          <TableHead key={h} className="text-[11px]">{h}</TableHead>
                        ))}
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {preview.sample_rows.map((r, i) => (
                        <TableRow key={i}>
                          {(preview.headers || []).map((h) => (
                            <TableCell key={h} className="text-[11px] whitespace-nowrap">
                              {r[h] === '' ? '—' : r[h]}
                            </TableCell>
                          ))}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>
            )}
          </div>
        )}

        {/* STEP 3 — Report */}
        {step === 3 && report && (
          <div className="space-y-4" data-testid="wizard-report">
            <div className="grid grid-cols-3 gap-2 text-center text-sm">
              <div className="p-3 bg-emerald-50 rounded">
                <div className="font-bold text-emerald-700 text-2xl">{report.imported ?? 0}</div>
                <div className="text-xs">Importés</div>
              </div>
              <div className="p-3 bg-amber-50 rounded">
                <div className="font-bold text-amber-700 text-2xl">{report.skipped ?? 0}</div>
                <div className="text-xs">Ignorés</div>
              </div>
              <div className="p-3 bg-red-50 rounded">
                <div className="font-bold text-red-700 text-2xl">{(report.errors || []).length}</div>
                <div className="text-xs">Erreurs</div>
              </div>
            </div>
            {report.dry_run && (
              <div className="text-xs bg-amber-50 border border-amber-200 rounded p-2 text-amber-800">
                Mode simulation — aucune donnée n&apos;a été insérée. Décochez « dry-run » pour importer réellement.
              </div>
            )}
            {(report.errors || []).length > 0 && (
              <div>
                <Label className="text-red-700">Lignes en erreur</Label>
                <div className="max-h-40 overflow-y-auto border border-red-100 rounded bg-red-50/40 mt-1">
                  <table className="w-full text-xs">
                    <thead className="bg-red-100 sticky top-0">
                      <tr><th className="p-1.5 text-left w-12">Ligne</th><th className="p-1.5 text-left">Raison</th></tr>
                    </thead>
                    <tbody>
                      {report.errors.map((e, i) => (
                        <tr key={i} className="border-t border-red-100">
                          <td className="p-1.5 font-mono">{e.row}</td>
                          <td className="p-1.5 text-red-800">{e.reason}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
            {(report.preview || []).length > 0 && (
              <div>
                <Label>Aperçu ({report.preview.length} premières lignes)</Label>
                <div className="max-h-40 overflow-y-auto border border-[#E2E8F0] rounded mt-1">
                  <table className="w-full text-xs">
                    <thead className="bg-[#F8FAFC] sticky top-0">
                      <tr>
                        <th className="p-1.5 text-left">Ligne</th>
                        <th className="p-1.5 text-left">Nom Prénom</th>
                        {kind === 'students' && <th className="p-1.5 text-left">Classe</th>}
                        {kind === 'teachers' && <th className="p-1.5 text-left">Matière</th>}
                        <th className="p-1.5 text-left">Avert.</th>
                      </tr>
                    </thead>
                    <tbody>
                      {report.preview.map((p, i) => (
                        <tr key={i} className="border-t border-[#E2E8F0]">
                          <td className="p-1.5 font-mono">{p.row}</td>
                          <td className="p-1.5 font-medium">{p.last_name} {p.first_name}</td>
                          {kind === 'students' && <td className="p-1.5">{p.class_id || '—'}</td>}
                          {kind === 'teachers' && <td className="p-1.5">{p.subject || '—'}</td>}
                          <td className="p-1.5 text-amber-700">
                            {(p.warnings || []).map((w, k) => <div key={k}>⚠ {w}</div>)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          {step === 1 && (
            <>
              <Button variant="outline" onClick={handleClose} disabled={busy}>Annuler</Button>
              <Button onClick={runPreview} disabled={!file || busy}
                className="bg-[#0052FF] hover:bg-[#0044D4]"
                data-testid="wizard-preview-btn">
                {busy ? 'Analyse…' : 'Analyser le fichier'}
                <ArrowRight size={16} className="ml-1" />
              </Button>
            </>
          )}
          {step === 2 && (
            <>
              <div className="flex-1">
                <input
                  type="text"
                  placeholder="Nommer & enregistrer ce mapping (optionnel)"
                  value={saveLabel}
                  onChange={(e) => setSaveLabel(e.target.value)}
                  className="h-9 rounded border border-[#E2E8F0] px-2 text-sm w-full"
                  maxLength={80}
                  data-testid="wizard-save-label-input"
                />
              </div>
              <Button variant="outline" onClick={() => setStep(1)} disabled={busy}>Retour</Button>
              <Button onClick={runFinal} disabled={busy}
                className="bg-emerald-600 hover:bg-emerald-700"
                data-testid="wizard-confirm-btn">
                <CheckCircle size={16} className="mr-1" />
                {busy ? 'Import…' : (dryRun ? 'Simuler' : 'Importer')}
              </Button>
            </>
          )}
          {step === 3 && (
            <>
              {report?.dry_run && (
                <Button variant="outline" onClick={() => { setStep(2); setReport(null); setDryRun(false); }}
                  data-testid="wizard-realimport-btn">
                  <XCircle size={16} className="mr-1" />
                  Refaire sans dry-run
                </Button>
              )}
              <Button onClick={handleClose} className="bg-[#0052FF] hover:bg-[#0044D4]"
                data-testid="wizard-close-btn">
                Fermer
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
